<template>
  <div class="timeline-item-tags tags">
    <div v-for="(item, index) in items" :key="index">
      <span class="tag is-rounded is-white" v-if="isString(item)">{{ item }}</span>
      <span class="tag is-rounded is-white" v-else-if="item.score === -1">{{ item.label }}
        <div class="tooltip-container timeline-item-tags-score-tooltip">
            <span>NA</span>
            <div class="tooltip tooltip-top " role="tooltip">
              <div class="tooltip-outer">
                <div class="tooltip-arrow"></div>
                <div class="tooltip-inner timeline-item-tags-score-tooltip-inner">{{ item.tooltip }}</div>
              </div>
            </div>
        </div>
      </span>
      <span class="tag is-rounded is-white" v-else-if="item.score">{{ item.label }}{{item.score}}</span>
      <div
        class="tag custom-meta-tag is-rounded is-white"
        :class="getCustomMetaTagClass(item.type)"
        v-else
      >
        <i class="mdi" :class="`mdi-${icons[item.type] || icons.custom_event }`"></i>
        <span>{{ getTitle(item) }}</span>
        <div class="tooltip tooltip-top" role="tooltip">
          <div class="tooltip-arrow"></div>
          <div class="tooltip-inner">
            <div class="level" v-for="(value, key) in item.values" :key="key">
              <div class="level-left">{{ startCase(key) }}</div>
              <div class="level-right has-text-weight-medium">{{ parseValue(value) }}</div>
            </div>
            <div class="level">
              <div class="level-item">{{ getTime(item) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div>
      <span
        class="tag is-rounded is-white"
        v-if="calories"
      >{{ $t("user.timeline.tag_calories", { amount: calories }) }}</span>
    </div>
  </div>
</template>

<script>
import { startCase, kebabCase, get } from "lodash";
import icons from "utils/icons";

export default {
  name: "TimelineItemTags",
  props: {
    items: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      icons,
      startCase
    };
  },
  computed: {
    calories() {
      const calories = this.items.reduce((acc, curr) => {
        if (this.isString(curr)) {
          return;
        }

        const calories = get(curr, "values.calories");

        if (calories && !isNaN(calories)) {
          return acc + calories;
        }

        return acc;
      }, 0);

      return calories;
    }
  },
  methods: {
    isString(item) {
      return typeof item === "string" || item instanceof String;
    },
    getTitle(item) {
      const values = Object.keys(item.values);
      const title = startCase(item.type);

      if (values.includes("calories")) {
        return `${title}: ${this.parseValue(item.values.calories)} cal`;
      }

      if (values.includes(item.type)) {
        return `${title}: ${this.parseValue(item.values[item.type])}`;
      }

      return title;
    },
    getCustomMetaTagClass(type) {
      return `custom-meta-tag-${kebabCase(type)}`;
    },
    parseValue(value) {
      return isNaN(value) ? value : Math.round(value * 100) / 100;
    },
    getTime(item) {
      const { _start, _end, end } = item;
      const startTime = _start.format("h:mm a");
      const endTime = end ? _end.format("h:mm a") : null;
      const formattedTime = endTime
        ? this.$t("user.timeline.tooltip_time_duration", {
            start: startTime,
            end: endTime
          })
        : this.$t("user.timeline.tooltip_time", { start: startTime });

      return formattedTime;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~scss/utilities/initial-variables.scss";
@import "~scss/utilities/derived-variables.scss";

.timeline-item-tags {
  margin-top: 0.25rem;
  margin-bottom: -0.25rem;

  > div {
    position: relative;

    &:not(:last-child) {
      margin-right: 0.25rem;
    }
  }
}

.timeline-item-tags-score-tooltip {
  margin-left: 0.3rem;
  cursor: help;
}

.timeline-item-tags-score-tooltip-inner {
  text-align: center;
  white-space: normal; 
  width: 26em; 
}

.tag {
  margin-bottom: 0.25rem;
  font-size: $size-small;
  line-height: 1.33;
  color: rgba($black, 0.87);
  border: 1px solid rgba($black, 0.08);
  transition: $transition;

  &:not(:last-child) {
    margin-right: 0.25rem;
  }

  i {
    font-size: 0.875rem;
    margin-right: 0.375rem;
    color: rgb(74, 74, 74);
  }
}

.custom-meta-tag {
  cursor: pointer;

  &-steps,
  &-floors {
    i {
      color: rgb(144, 19, 254);
    }
  }

  &-beverages {
    i {
      color: #4284ec;
    }
  }

  &-ecg,
  &-ecg-nsr,
  &-hr,
  &-pressure,
  &-glucose {
    i {
      color: rgb(239, 83, 80);
    }
  }

  &-recreational-product {
    i {
      color: rgb(245, 166, 35);
    }
  }

  &:hover,
  &:focus {
    box-shadow: 0 2px 4px 0 rgba(black, 0.08);

    .tooltip {
      opacity: 1;
    }
  }
}

.tooltip {
  width: auto;
  bottom: calc(100% + 0.5rem);
  font-size: $size-small;

  .level {
    &:not(:last-child) {
      margin-bottom: 0.75rem;
    }
  }

  .level-left {
    & + .level-right {
      margin-left: 1.75rem;
    }
  }
}

.tooltip-inner {
  padding: 0.75rem;
}

.level-item {
  color: rgba($black, 0.5);
}
</style>
